import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '@/App';
import axios from 'axios';
import {
  Calendar, Upload, Trash2, RefreshCw, FileText, CheckCircle,
  Loader2, ExternalLink, Link as LinkIcon, Copy, Info, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { PROVIDER_GUIDES, OWNER_SETUP } from '@/components/calendarProviders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── ICS parser ──
const parseICS = (text) => {
  const events = [];
  const unfolded = text.replace(/\r?\n[ \t]/g, '');            // RFC 5545 line folding
  const blocks = unfolded.split('BEGIN:VEVENT');
  const unescape = (v) => (v || '').replace(/\\n/gi, ' ').replace(/\\,/g, ',').replace(/\\;/g, ';').trim();
  for (let i = 1; i < blocks.length; i++) {
    const block = blocks[i].split('END:VEVENT')[0].replace(/BEGIN:VALARM[\s\S]*?END:VALARM/g, '');
    const get = (key) => {
      const m = block.match(new RegExp(`(?:^|\\n)${key}(?:;[^:\\r\\n]*)?:([^\\r\\n]*)`));
      return m ? m[1].trim() : null;
    };
    // 'Z' = UTC. Floating and TZID times are treated as the viewer's local time.
    const parseDate = (raw) => {
      if (!raw) return null;
      const m = raw.match(/^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})?)?(Z)?$/);
      if (!m) return null;
      const [, y, mo, d, h = '00', mi = '00', , z] = m;
      const date = z ? new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi)) : new Date(+y, +mo - 1, +d, +h, +mi);
      return date.toISOString();
    };
    const title = unescape(get('SUMMARY')) || 'Untitled Event';
    const start = parseDate(get('DTSTART'));
    let end = parseDate(get('DTEND') || get('DTSTART'));
    if (start && (!end || new Date(end) <= new Date(start))) end = new Date(new Date(start).getTime() + 30 * 60000).toISOString();
    if (start) events.push({ title, start_time: start, end_time: end, description: unescape(get('DESCRIPTION')) || null, source: 'ics' });
  }
  return events;
};

// ── Block card ──
const BlockCard = ({ block, onDelete }) => {
  const start = new Date(block.start_time);
  const end   = new Date(block.end_time);
  const srcColors = { google: '#4285F4', outlook: '#0078D4', apple: '#555', ics: '#22c55e' };
  const srcLabels = { google: 'Google', outlook: 'Outlook', apple: 'Apple', ics: 'ICS' };
  const color = srcColors[block.source] || '#6b7280';
  const label = srcLabels[block.source] || block.source;
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl group"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="w-2 h-8 rounded-full flex-shrink-0" style={{ background: color }}/>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">{block.title}</p>
        <p className="text-xs text-gray-500 mt-0.5">
          {start.toLocaleDateString('en',{weekday:'short',month:'short',day:'numeric'})} ·{' '}
          {start.toLocaleTimeString('en',{hour:'2-digit',minute:'2-digit'})} –{' '}
          {end.toLocaleTimeString('en',{hour:'2-digit',minute:'2-digit'})}
          <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold"
            style={{ background: `${color}18`, color }}>{label}</span>
        </p>
      </div>
      <button onClick={() => onDelete(block.id)}
        className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-400/10 transition-all">
        <Trash2 size={13}/>
      </button>
    </div>
  );
};

// ── Accordion provider section ──
const ProviderSection = ({ icon, title, color, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="glass-card overflow-hidden mb-4">
      <button onClick={() => setOpen(o => !o)} data-testid={`provider-section-${title.split(' ')[0].toLowerCase()}`}
        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-white/3 transition-colors text-left">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `${color}15`, border: `1px solid ${color}25` }}>
          {icon}
        </div>
        <span className="flex-1 text-sm font-semibold text-white">{title}</span>
        {open ? <ChevronUp size={15} className="text-gray-500"/> : <ChevronDown size={15} className="text-gray-500"/>}
      </button>
      {open && <div className="px-5 pb-5 border-t border-white/5 pt-4">{children}</div>}
    </div>
  );
};

// ── Copy button ──
const CopyBtn = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };
  return (
    <button onClick={copy}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-all"
      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: copied ? '#22c55e' : '#9ca3af' }}>
      {copied ? <CheckCircle size={12}/> : <Copy size={12}/>}
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
};

// ── Step ──
const Step = ({ n, text }) => (
  <div className="flex items-start gap-3 mb-3">
    <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5"
      style={{ background: 'rgba(255,255,255,0.08)', color: '#9ca3af' }}>{n}</div>
    <p className="text-sm text-gray-300 leading-relaxed">{text}</p>
  </div>
);

// ── Main CalendarSync page ──
const GOOGLE_ICON = <svg viewBox="0 0 24 24" width="16" height="16"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>;
const OUTLOOK_ICON = <svg viewBox="0 0 24 24" width="16" height="16"><rect width="24" height="24" rx="4" fill="#0078D4"/><text x="6" y="17" fontSize="13" fontWeight="bold" fill="white">O</text></svg>;
const APPLE_ICON = <svg viewBox="0 0 24 24" width="16" height="16"><rect width="24" height="24" rx="6" fill="#1C1C1E"/><text x="5" y="17" fontSize="12" fontWeight="bold" fill="#ddd">31</text></svg>;

const SourceLink = ({ source }) => (
  <a href={source.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-cyan-400/80 hover:text-cyan-300 mb-3">
    <ExternalLink size={11}/> Official guide: {source.label}
  </a>
);

const Notes = ({ notes }) => (notes || []).map((n, i) => (
  <div key={i} className="flex items-start gap-2 text-[11px] text-gray-500 mb-1.5"><Info size={11} className="flex-shrink-0 mt-0.5"/><span>{n}</span></div>
));

const OAuthBlock = ({ provider, status, color, onConnect, onSync, onDisconnect, busy }) => {
  const guide = PROVIDER_GUIDES[provider];
  const owner = OWNER_SETUP[provider];
  const [showOwner, setShowOwner] = useState(false);
  const redirect = `${BACKEND_URL}/api/calendar/oauth/${provider}/callback`;
  const name = provider === 'google' ? 'Google' : 'Microsoft';
  return (
    <div className="mb-5" data-testid={`${provider}-oauth-block`}>
      <p className="text-xs text-gray-300 font-semibold mb-1.5">{guide.oauthTitle}</p>
      <p className="text-xs text-gray-400 mb-3">{guide.oauthText}</p>
      {status?.connected ? (
        <div className="rounded-xl p-3 mb-2" style={{ background: `${color}10`, border: `1px solid ${color}30` }} data-testid={`${provider}-connected`}>
          <p className="text-xs text-white flex items-center gap-1.5"><CheckCircle size={13} className="text-green-400"/> Connected as {status.account || 'your account'}</p>
          <p className="text-[11px] text-gray-500 mt-1">{status.events} events · last synced {status.last_synced ? new Date(status.last_synced).toLocaleString() : 'never'}</p>
          <div className="flex gap-2 mt-2">
            <button onClick={onSync} disabled={busy} data-testid={`${provider}-sync-btn`} className="text-xs px-3 py-1.5 rounded-lg text-gray-200 border border-white/10 hover:bg-white/5 flex items-center gap-1">
              {busy ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>} Sync now
            </button>
            <button onClick={onDisconnect} data-testid={`${provider}-disconnect-btn`} className="text-xs px-3 py-1.5 rounded-lg text-red-300 border border-red-400/20 hover:bg-red-400/10">Disconnect</button>
          </div>
        </div>
      ) : (
        <button onClick={onConnect} disabled={busy || !status?.configured} data-testid={`${provider}-connect-btn`}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed mb-2"
          style={{ background: `${color}1f`, border: `1px solid ${color}4d`, color }}>
          {busy ? <Loader2 size={15} className="animate-spin"/> : <ExternalLink size={15}/>}
          {status?.configured ? `Connect with ${name}` : `Connect with ${name} — not configured yet`}
        </button>
      )}
      {!status?.configured && (
        <div className="text-[11px] text-amber-300/80 mb-2" data-testid={`${provider}-not-configured`}>
          One-click connect turns on once the app owner adds {name} OAuth credentials. Until then, use the link option below.
          <button onClick={() => setShowOwner(v => !v)} className="ml-1 underline" data-testid={`${provider}-owner-setup-toggle`}>{showOwner ? 'Hide' : 'Show'} owner setup</button>
        </div>
      )}
      {showOwner && (
        <div className="rounded-xl p-3 mb-2 text-xs" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid={`${provider}-owner-setup`}>
          <p className="text-gray-300 font-semibold mb-1">{owner.title}</p>
          <SourceLink source={owner.source}/>
          {owner.steps.map((t, i) => <Step key={i} n={i + 1} text={t}/>)}
          <div className="flex items-center gap-2 mt-1">
            <code className="flex-1 truncate text-[11px] text-gray-400 bg-black/30 rounded px-2 py-1">{redirect}</code>
            <CopyBtn text={redirect}/>
          </div>
        </div>
      )}
    </div>
  );
};

const LinkBlock = ({ provider, sourceKey, color, onSubscribe }) => {
  const guide = PROVIDER_GUIDES[provider];
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => { setBusy(true); const ok = await onSubscribe(url, sourceKey); setBusy(false); if (ok) setUrl(''); };
  return (
    <div className="mb-5" data-testid={`${provider}-link-block`}>
      <p className="text-xs text-gray-300 font-semibold mb-1">{guide.linkTitle}</p>
      <SourceLink source={guide.linkSource}/>
      {guide.linkSteps.map((t, i) => <Step key={i} n={i + 1} text={t}/>)}
      <Notes notes={guide.linkNotes}/>
      <div className="flex gap-2 mt-3">
        <input value={url} onChange={e => setUrl(e.target.value)} placeholder={guide.linkPlaceholder} data-testid={`${provider}-link-input`}
          className="flex-1 rounded-xl px-3 py-2.5 text-sm text-white placeholder-gray-600 outline-none"
          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}/>
        <button onClick={submit} disabled={busy || !url.trim()} data-testid={`${provider}-link-submit`}
          className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40 flex items-center gap-1.5"
          style={{ background: `${color}33`, border: `1px solid ${color}59` }}>
          {busy ? <Loader2 size={14} className="animate-spin"/> : <LinkIcon size={14}/>} Subscribe
        </button>
      </div>
    </div>
  );
};

const FileBlock = ({ provider }) => {
  const guide = PROVIDER_GUIDES[provider];
  if (!guide.fileTitle) return null;
  return (
    <div className="pt-4 border-t border-white/5" data-testid={`${provider}-file-block`}>
      <p className="text-xs text-gray-300 font-semibold mb-1">{guide.fileTitle}</p>
      <SourceLink source={guide.fileSource}/>
      {guide.fileSteps.map((t, i) => <Step key={i} n={i + 1} text={t}/>)}
    </div>
  );
};

const SubscriptionRow = ({ sub, onSync, onDelete }) => (
  <div className="flex items-center gap-3 p-3 rounded-xl" data-testid={`subscription-${sub.id}`}
    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
    <LinkIcon size={14} className="text-gray-500 flex-shrink-0"/>
    <div className="flex-1 min-w-0">
      <p className="text-sm text-white truncate">{sub.name}</p>
      <p className="text-[11px] text-gray-500">{sub.events} events{sub.skipped_all_day ? ` · ${sub.skipped_all_day} all-day skipped` : ''} · synced {new Date(sub.last_synced).toLocaleString()}</p>
    </div>
    <button onClick={() => onSync(sub.id)} data-testid={`subscription-sync-${sub.id}`} className="p-1.5 rounded-lg text-gray-500 hover:text-cyan-300"><RefreshCw size={13}/></button>
    <button onClick={() => onDelete(sub.id)} data-testid={`subscription-delete-${sub.id}`} className="p-1.5 rounded-lg text-gray-500 hover:text-red-400"><Trash2 size={13}/></button>
  </div>
);

const CalendarSync = () => {
  const { auth } = useContext(AuthContext);
  const headers = { Authorization: `Bearer ${auth.token}` };
  const [blocks, setBlocks] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [busyProvider, setBusyProvider] = useState(null);
  const [parsePreview, setParsePreview] = useState(null);
  const fileRef = useRef();

  const loadAll = async () => {
    try {
      const [b, p] = await Promise.all([
        axios.get(`${API}/calendar/blocks`, { headers }),
        axios.get(`${API}/calendar/providers`, { headers }),
      ]);
      setBlocks(b.data || []);
      setStatus(p.data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('provider')) {
      const name = q.get('provider') === 'google' ? 'Google Calendar' : 'Outlook';
      if (q.get('connected')) toast.success(`${name} connected — ${q.get('events')} events imported`);
      else if (q.get('error')) toast.error(`${name} wasn't connected: ${q.get('error')}`);
      window.history.replaceState({}, '', '/calendar-sync');
    }
    loadAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const errMsg = (e, fallback) => e.response?.data?.detail || fallback;

  const connect = async (provider) => {
    setBusyProvider(provider);
    try {
      const res = await axios.get(`${API}/calendar/oauth/${provider}/start`, { headers, params: { origin_url: window.location.origin } });
      window.location.href = res.data.url;
    } catch (e) { toast.error(errMsg(e, 'Could not start sign-in')); setBusyProvider(null); }
  };

  const syncConnection = async (provider) => {
    setBusyProvider(provider);
    try {
      const res = await axios.post(`${API}/calendar/connections/${provider}/sync`, {}, { headers });
      toast.success(`Synced ${res.data.events} events`);
      loadAll();
    } catch (e) { toast.error(errMsg(e, 'Sync failed')); }
    finally { setBusyProvider(null); }
  };

  const disconnect = async (provider) => {
    try {
      const res = await axios.delete(`${API}/calendar/connections/${provider}`, { headers });
      toast.success(`Disconnected — removed ${res.data.removed_events} imported events`);
      loadAll();
    } catch (e) { toast.error(errMsg(e, 'Could not disconnect')); }
  };

  const subscribe = async (url, provider) => {
    try {
      const res = await axios.post(`${API}/calendar/subscriptions`, { url, provider }, { headers });
      toast.success(`Subscribed — ${res.data.events} upcoming events imported${res.data.skipped_all_day ? ` (${res.data.skipped_all_day} all-day events don't block time)` : ''}`);
      loadAll();
      return true;
    } catch (e) { toast.error(errMsg(e, 'Could not subscribe to that link')); return false; }
  };

  const resyncSub = async (id) => {
    try {
      const res = await axios.post(`${API}/calendar/subscriptions/${id}/sync`, {}, { headers });
      toast.success(`Synced ${res.data.events} events`);
      loadAll();
    } catch (e) { toast.error(errMsg(e, 'Sync failed')); }
  };

  const deleteSub = async (id) => {
    try {
      const res = await axios.delete(`${API}/calendar/subscriptions/${id}`, { headers });
      toast.success(`Removed subscription and ${res.data.removed_events} events`);
      loadAll();
    } catch (e) { toast.error(errMsg(e, 'Could not remove')); }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.match(/\.(ics|ical)$/i)) { toast.error('Please upload an .ics file (unzip Google exports first)'); return; }
    const parsed = parseICS(await file.text());
    if (!parsed.length) { toast.error('No events found in this file'); return; }
    setParsePreview({ events: parsed, filename: file.name });
    e.target.value = '';
  };

  const confirmImport = async () => {
    if (!parsePreview) return;
    setImporting(true);
    try {
      const res = await axios.post(`${API}/calendar/import`, parsePreview.events.map(ev => ({ ...ev, source: 'ics' })), { headers });
      toast.success(`Imported ${res.data.imported} events ✅`);
      setParsePreview(null);
      loadAll();
    } catch { toast.error('Import failed. Try again.'); }
    finally { setImporting(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/calendar/blocks/${id}`, { headers });
      setBlocks(prev => prev.filter(b => b.id !== id));
    } catch { toast.error('Could not remove'); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#0A0E27,#050816)' }}>
      <Loader2 size={28} className="text-cyan-400 animate-spin"/>
    </div>
  );

  const subs = status?.subscriptions || [];

  return (
    <div className="min-h-screen pt-20 px-4 pb-16" style={{ background: 'linear-gradient(135deg,#0A0E27,#050816)' }}>
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#4285F4,#34A853)' }}>
              <Calendar size={16} color="#fff"/>
            </div>
            <h1 className="heading-font text-2xl font-bold text-white">Calendar Sync</h1>
          </div>
          <p className="text-gray-400 text-sm">Bring in your existing events so SpaceCal.ai schedules around your real life. Imported events are read-only busy time — SpaceCal never edits your calendars. All-day events don't block time.</p>
        </div>

        {parsePreview && (
          <div className="glass-card p-5 mb-6" style={{ border: '1px solid rgba(34,197,94,0.25)' }} data-testid="ics-preview">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle size={16} className="text-green-400 flex-shrink-0"/>
              <div>
                <p className="text-sm text-white font-semibold">{parsePreview.filename}</p>
                <p className="text-xs text-green-400">{parsePreview.events.length} events ready to import</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setParsePreview(null)} className="flex-1 py-2.5 rounded-xl text-sm text-gray-400 hover:text-white"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}>Cancel</button>
              <button onClick={confirmImport} disabled={importing} data-testid="ics-import-confirm"
                className="flex-1 teal-btn py-2.5 text-sm font-semibold flex items-center justify-center gap-2">
                {importing ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>}
                {importing ? 'Importing…' : `Import ${parsePreview.events.length} events`}
              </button>
            </div>
          </div>
        )}

        <ProviderSection icon={GOOGLE_ICON} title="Google Calendar" color="#4285F4" defaultOpen>
          <OAuthBlock provider="google" status={status?.google} color="#4285F4" busy={busyProvider === 'google'}
            onConnect={() => connect('google')} onSync={() => syncConnection('google')} onDisconnect={() => disconnect('google')}/>
          <LinkBlock provider="google" sourceKey="google" color="#4285F4" onSubscribe={subscribe}/>
          <FileBlock provider="google"/>
        </ProviderSection>

        <ProviderSection icon={OUTLOOK_ICON} title="Microsoft Outlook / Microsoft 365" color="#0078D4">
          <OAuthBlock provider="microsoft" status={status?.microsoft} color="#0078D4" busy={busyProvider === 'microsoft'}
            onConnect={() => connect('microsoft')} onSync={() => syncConnection('microsoft')} onDisconnect={() => disconnect('microsoft')}/>
          <LinkBlock provider="microsoft" sourceKey="outlook" color="#0078D4" onSubscribe={subscribe}/>
        </ProviderSection>

        <ProviderSection icon={APPLE_ICON} title="Apple iCloud Calendar" color="#9CA3AF">
          <div className="flex items-start gap-2 p-3 rounded-xl text-xs text-gray-400 mb-4" data-testid="apple-oauth-note"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <Info size={12} className="flex-shrink-0 mt-0.5"/><span>{PROVIDER_GUIDES.apple.oauthNote}</span>
          </div>
          <LinkBlock provider="apple" sourceKey="apple" color="#9CA3AF" onSubscribe={subscribe}/>
          <FileBlock provider="apple"/>
        </ProviderSection>

        <ProviderSection icon={<FileText size={16} className="text-green-400"/>} title="Upload an exported .ics file" color="#22c55e">
          <p className="text-xs text-gray-400 mb-4">Use this with the export steps above. A file is a one-time snapshot — subscribe with a link to stay in sync.</p>
          <input ref={fileRef} type="file" accept=".ics,.ical" onChange={handleFile} className="hidden" data-testid="ics-file-input"/>
          <button onClick={() => fileRef.current?.click()} data-testid="ics-upload-btn"
            className="w-full flex flex-col items-center justify-center gap-3 py-8 rounded-xl border-2 border-dashed transition-colors hover:border-green-500/40"
            style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
            <Upload size={22} className="text-gray-500"/>
            <p className="text-sm text-gray-300">Click to upload .ics file</p>
          </button>
        </ProviderSection>

        {subs.length > 0 && (
          <div className="mb-6" data-testid="subscriptions-list">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Subscribed calendars ({subs.length})</h2>
            <div className="space-y-2">{subs.map(s => <SubscriptionRow key={s.id} sub={s} onSync={resyncSub} onDelete={deleteSub}/>)}</div>
          </div>
        )}

        {blocks.length > 0 && (
          <div data-testid="imported-events">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Imported events ({blocks.length})</h2>
              <button onClick={loadAll} className="text-gray-600 hover:text-gray-300 p-1"><RefreshCw size={13}/></button>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
              {[...blocks].sort((a, b) => a.start_time.localeCompare(b.start_time)).map(b => <BlockCard key={b.id} block={b} onDelete={handleDelete}/>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarSync;
